import { useState, useCallback, useRef, SetStateAction, Dispatch } from 'react';

import { useRecommendations, AppRecRecommendationResult } from '@atlassian/app-recommendations-kit';

import {
	CCPKey,
	CCPKeyToProductIdType,
	NUM_DISPLAY_RECOMMENDATIONS,
	ccpKeyToProductId,
} from '../common/constants';

import { useAnalytics } from './use-analytics';

const WAC_GLOBAL_NAV_PRODUCT_RECOMMENDATION_USE_CASE = 'WACGlobalNavProductRecommendation';

export function useFetchRecommendations() {
	const [isRecommendationLoading, setIsRecommendationLoading] = useState(false);
	const isRecommendationLoadedRef = useRef(false);
	const [recommendations, setRecommendations] = useState<CCPKey[]>([]);
	const { fireFetchGrowthRecommendationSucceeded, fireFetchGrowthRecommendationFailed } =
		useAnalytics();

	const { getRecommendations } = useRecommendations({
		endpoint:
			process.env['NODE_ENV'] === 'development'
				? 'https://wac.stg.internal.atlassian.com/gateway/api/graphql' // use staging endpoint
				: undefined, // use default endpoint
		contextResolver: {
			product: 'marketing',
			useCase: WAC_GLOBAL_NAV_PRODUCT_RECOMMENDATION_USE_CASE,
		},
	});

	const getProductRecommendations = useCallback(
		async ({
			setIsGlobalNavProductRecommendationsExperimentFailed,
		}: {
			setIsGlobalNavProductRecommendationsExperimentFailed: Dispatch<SetStateAction<boolean>>;
		}) => {
			if (isRecommendationLoading || isRecommendationLoadedRef.current) {
				return;
			}

			const startTime = performance.now();

			try {
				setIsRecommendationLoading(true);

				const result = await getRecommendations({
					first: NUM_DISPLAY_RECOMMENDATIONS,
					rerank: asEntityIdList(ccpKeyToProductId),
					config: {
						cacheConfig: {
							ttl: 60,
						},
					},
				});

				const { response: { data } = {} } = result;

				if (isValidResponse(data)) {
					const recommendedProductIds = data.map(({ entityId }) => entityId as CCPKey);
					setRecommendations(recommendedProductIds);

					const elapsedTime = performance.now() - startTime;
					fireFetchGrowthRecommendationSucceeded(recommendedProductIds, elapsedTime);

					setIsRecommendationLoading(false);
					isRecommendationLoadedRef.current = true;
				} else {
					throw new Error('Invalid response from App Recommendations API');
				}
			} catch (error) {
				const elapsedTime = performance.now() - startTime;
				const errorMessage = error instanceof Error ? error.message : String(error);
				fireFetchGrowthRecommendationFailed(errorMessage, elapsedTime);

				setIsGlobalNavProductRecommendationsExperimentFailed(true);
				setIsRecommendationLoading(false);
				isRecommendationLoadedRef.current = true;
			}
		},
		[
			fireFetchGrowthRecommendationFailed,
			fireFetchGrowthRecommendationSucceeded,
			getRecommendations,
			isRecommendationLoading,
		],
	);

	return {
		getProductRecommendations,
		isRecommendationLoading,
		recommendations,
	};
}

export const asEntityIdList = (entities: CCPKeyToProductIdType) =>
	Object.keys(entities).map((key) => ({ entityId: key }));

/**
 * Ensure the response is valid type
 */
const isValidResponse = (
	data: AppRecRecommendationResult | undefined,
): data is AppRecRecommendationResult => {
	return Array.isArray(data);
};
